import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { GeneralDrawer } from 'sharedComponents';
import { translate } from 'helpers/translateHelper';
import { IconClose, IconExpand, IconHelpCircle, IconPlus, IconTrendComment } from 'svgIcons/MotionPortalIcons';
import { GeneralButton } from 'sharedComponents/GeneralButton';
import { SearchBox } from 'sharedComponents/SearchBox';
import TrendCommentsList from './TrendCommentsList';
import { cloneDeep } from 'lodash';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import { formatDateTime } from 'helpers/dateTimeFormatter';

const TrendCommentsDrawer = ({
    isShowDrawer,
    setIsShowDrawer,
    setIsShowModal,
    trendCommentsList,
    setDeleteCommentId,
    trendCommentsListLoadingStatus,
    trendKPIsList,
    selectedDataPointTimestamp,
    resetTrendCommentSelection,
    setIsShowHelpModal
}) => {
    const [searchFilter, setSearchFilter] = useState('');
    const [filteredList, setFilteredList] = useState(cloneDeep(trendCommentsList));

    const filterAssets = (searchValue) => {
        let filteredList = cloneDeep(trendCommentsList);
        if (selectedDataPointTimestamp) {
            const timestamps = selectedDataPointTimestamp.split(' | ');
            if (timestamps.length === 2) {
                const from = timestamps[0].trim();
                const to = timestamps[1].trim();
                filteredList = trendCommentsList?.filter(comment =>
                    comment.timestamp >= from && comment.timestamp <= to
                );
            } else {
                const timestamp = selectedDataPointTimestamp.trim();
                filteredList = trendCommentsList?.filter(comment =>
                    comment.timestamp === timestamp
                );
            }
        }
        if (searchValue) {
            filteredList = filteredList?.filter(comment =>
                comment.subjectText?.toLowerCase().includes(searchValue.toLowerCase())
            );
        }
        return filteredList;
    };

    const handleSearch = (value) => {
        setSearchFilter(value);
        setFilteredList(filterAssets(value));
    };

    useEffect(() => {
        setFilteredList(filterAssets(searchFilter));
    }, [trendCommentsList, selectedDataPointTimestamp]);

    const renderNoComments = () =>
        <div className='no-comments'>
            <span>{translate('ABB.Powertrain.Frontend.opTrendNoComments')}</span>
            <span>{translate('ABB.Powertrain.Frontend.opTrendAddCommentFromDrawer')}</span>
            <GeneralButton
                className='add-comment-button'
                type='primary'
                onClick={() => setIsShowModal(true)}
                text={<><IconPlus />{translate('ABB.Powertrain.Frontend.opTrendCommentsModalTitle')}</>}
            />
        </div>;

    const renderNoCommentsFromSearch = () =>
        <div className='no-comments-search'>
            <span>{translate('ABB.Powertrain.Frontend.opTrendNoCommentsMatchSearch')}</span>
        </div>;

    const formatSelectedTimestamp = () => {
        const timestamps = selectedDataPointTimestamp.split(' | ');
        if (timestamps.length === 2) {
            return `${formatDateTime(timestamps[0].trim())} - ${formatDateTime(timestamps[1].trim())}`;
        }
        return formatDateTime(selectedDataPointTimestamp);
    };

    const renderContent = () =>
        <>
            <div className='comment-drawer-body'>
                {trendCommentsList?.length ?
                    <div className='comment-body-container'>
                        <div className='comment-header'>
                            <SearchBox
                                placeholder={translate('ABB.Powertrain.Frontend.assetSearchButton')}
                                onChange={handleSearch}
                                value={searchFilter}
                            />
                            <div className='header-right'>
                                <GeneralButton
                                    className='add-comment-button'
                                    type='normal'
                                    text={<><IconPlus />{translate('ABB.Powertrain.Frontend.opTrendCommentsModalAcceptBtn')}</>}
                                    onClick={() => setIsShowModal(true)}
                                />
                                <button type='button' className='trend-comments-help-button' onClick={() => setIsShowHelpModal(true)} aria-label='show trend comments help'>
                                    <IconHelpCircle
                                        width={'16px'}
                                        height={'16px'}
                                    />
                                </button>
                            </div>
                        </div>
                        {selectedDataPointTimestamp &&
                            <div className='selected-data-point'>
                                <span>
                                    {formatSelectedTimestamp()}
                                </span>
                                <button type='button' className='close-icon' onClick={resetTrendCommentSelection} aria-label='clear selected data point'><IconClose /></button>
                            </div>
                        }
                        {filteredList?.length ?
                            <TrendCommentsList
                                trendCommentsList={filteredList}
                                setIsShowModal={setIsShowModal}
                                setDeleteCommentId={setDeleteCommentId}
                                trendKPIsList={trendKPIsList}
                            />
                            : renderNoCommentsFromSearch()
                        }
                    </div>
                    : renderNoComments()
                }
            </div>
            <div className='comment-drawer-footer'>
                <button
                    type='button'
                    aria-label={'expand sidebar'}
                    className={'column-sidebar-toggle-button'}
                    onClick={() => setIsShowDrawer(false)}
                >
                    <IconExpand width={16} height={16} />
                </button>
            </div>
        </>;

    return (
        <GeneralDrawer
            show={isShowDrawer}
            close={() => setIsShowDrawer(false)}
            title={translate('ABB.Powertrain.Frontend.opTrendCommentsTitle')}
            closeButton={true}
            backdrop={false}
            scroll={true}
            headerIcon={<IconTrendComment />}
            drawerClassName={'trend-comments-drawer'}
        >
            <LoadingStatusWrapper
                useCustomAnchor
                transparent
                loadingStatus={[trendCommentsListLoadingStatus]}
            >
                {renderContent()}
            </LoadingStatusWrapper>
        </GeneralDrawer>
    );
};

TrendCommentsDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    comments: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        text: PropTypes.string.isRequired
    })).isRequired,
    onAddComment: PropTypes.func.isRequired,
    onEditComment: PropTypes.func.isRequired,
    onDeleteComment: PropTypes.func.isRequired
};

export default TrendCommentsDrawer;
